import { toVarRef } from '@chakra-ui/theme-tools'
import { brand, system } from '@workday/canvas-tokens-web'
import { StylesConfig, GroupBase } from 'react-select'
import { merge } from 'lodash'
import { useTheme } from '../../design_system'
import colors from '../colors'

// Extend the Props interface in the react-select module (for TypeScript)
declare module 'react-select/dist/declarations/src/Select' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
    isReadOnly?: boolean
    isInvalid?: boolean
    isWarned?: boolean
  }
}

// Canvas shape.md (8px flag-ON, polyfilled to the legacy 0.25rem flag-OFF) — same
// radius token Input.js uses so a Select and an Input read as the same field.
const fieldRadius = `var(${system.shape.md})`

type ChakraTypeScales = {
  textStyles?: Record<string, Record<string, string | number>>
  fontSizes?: Record<string, string>
  lineHeights?: Record<string, string | number>
  fontWeights?: Record<string, string | number>
  letterSpacings?: Record<string, string>
}

/**
 * Resolve a Chakra textStyle to concrete CSS for react-select, which is styled via emotion
 * and can't consume Chakra scale keys (`fontSize: 'md'`). Flag-OFF the ramp holds scale keys
 * → map them through the theme scales; flag-ON it already holds `var(--cnvs-*)` refs → the
 * lookups miss and the raw value passes through. Either way the result matches what Chakra
 * computes for <Input> from the same textStyle, so Select text rides the Canvas type ramp.
 * Temporary: once the legacy scale-key ramp is gone at FF removal, this can be dropped and
 * the var-based textStyle spread into emotion directly.
 */
export function resolveTextStyle(theme: Record<string, unknown>, name: string): Record<string, string | number> {
  const { textStyles, fontSizes, lineHeights, fontWeights, letterSpacings } = theme as ChakraTypeScales
  const style = textStyles?.[name] ?? {}
  const resolved: Record<string, string | number> = {}
  if (style.fontFamily != null) resolved.fontFamily = style.fontFamily
  if (style.fontSize != null) resolved.fontSize = fontSizes?.[style.fontSize] ?? style.fontSize
  if (style.fontWeight != null) resolved.fontWeight = fontWeights?.[style.fontWeight] ?? style.fontWeight
  if (style.lineHeight != null) resolved.lineHeight = lineHeights?.[style.lineHeight] ?? style.lineHeight
  if (style.letterSpacing != null) resolved.letterSpacing = letterSpacings?.[style.letterSpacing] ?? style.letterSpacing
  return resolved
}

/**
 * Default style for the `react-select` component.
 */
export const useSelectStyle = <Option, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>(
  styles?: StylesConfig<Option, IsMulti, Group>
): StylesConfig<Option, IsMulti, Group> => {
  const theme = useTheme()
  const { zIndices } = theme
  const bodyType = resolveTextStyle(theme, 'body')

  // Rebrand marker set on workdayThemeSanaRebrand — emotion can't read the feature flag
  // from a Chakra theme fn, so the Canvas-only menu treatment is gated on this. Flag-OFF
  // the radii scale has neither `x2` nor `round`, so react-select keeps its legacy shape.
  const radii = theme.radii as Record<string, string> | undefined
  const menuRadius = radii?.x2
  const optionRadius = radii?.round

  // Canvas focus-outline token (the value the legacy --vndly-color-input-*-focused vars
  // already resolved to in both flag states) — used direct so no middleman var survives.
  const focusOutline = `var(${brand.common.focusOutline})`

  // Field text rides the semantic `fg.*` tokens ungated — flag-OFF they polyfill to the exact
  // values the legacy input vars used (legacyWorkdayTheme L1). Exception: the selected value was
  // legacy-rendered in container-dark (gray-800), not input-text, so `valueColor` is gated to
  // avoid a #222 → #3b3b3b flag-OFF shift.
  const valueColor = theme.isSanaRebrand ? `var(${system.color.fg.default})` : 'var(--vndly-color-container-dark)'
  const inputColor = `var(${system.color.fg.default})`
  const placeholderColor = `var(${system.color.fg.muted.default})`
  const disabledValueColor = `var(${system.color.fg.disabled})`

  return {
    ...styles,
    control: (provided, state) => {
      const baseStyles = merge(
        provided,
        {
          ...bodyType,
          borderColor: `var(${system.color.border.input.default})`,
          borderRadius: fieldRadius
        },
        state.selectProps.isInvalid && {
          borderColor: `var(${system.color.brand.border.critical})`,
          borderWidth: '2px',
          ...(state.isFocused && {
            boxShadow: focusRing(focusOutline),
            outline: 'none'
          }),
          '&:hover': {
            borderColor: `var(${system.color.brand.border.critical})`
          }
        },
        state.selectProps.isWarned && {
          borderColor: `var(${brand.common.caution.inner})`,
          borderWidth: '2px',
          ...(state.isFocused && {
            // Warning is signalled by the amber 2px border; the focus ring stays the
            // blue focus-outline (matching the plain- and invalid-focus rings) so
            // "focused" reads consistently across every field state.
            boxShadow: focusRing(focusOutline),
            outline: 'none'
          }),
          '&:hover': {
            borderColor: `var(${brand.common.caution.inner})`
          }
        },
        !state.isFocused &&
          !state.selectProps.isWarned &&
          !state.selectProps.isInvalid &&
          !state.selectProps.isDisabled &&
          !state.selectProps.isReadOnly && {
            '&:hover': {
              borderColor: `var(${system.color.border.input.hover})`,
              boxShadow: 'none'
            }
          },
        state.isFocused &&
          !state.selectProps.isWarned &&
          !state.selectProps.isInvalid && {
            borderColor: focusOutline,
            boxShadow: `0 0 0 1px ${focusOutline}`
          },
        state.selectProps.isReadOnly && {
          backgroundColor: `var(${system.color.bg.alt.softer})`,
          ...(!state.isFocused && {
            '&:hover': {
              borderColor: `var(${system.color.border.input.default})`,
              boxShadow: 'none'
            }
          })
        },
        state.selectProps.isDisabled && {
          cursor: 'not-allowed',
          backgroundColor: `var(${system.color.bg.alt.softer})`,
          borderColor: `var(${system.color.fg.disabled})`
        }
      )

      // Pass the merged baseStyles to the external styles function
      return merge(baseStyles, styles?.control?.(baseStyles, state))
    },

    placeholder: (provided, state) => {
      const baseStyles = merge(
        provided,
        {
          color: placeholderColor
        },
        state.selectProps.isReadOnly && {
          display: 'none'
        },
        state.isDisabled && {
          color: placeholderColor
        }
      )
      return merge(baseStyles, styles?.placeholder?.(baseStyles, state))
    },

    input: (provided, state) => {
      const baseStyles = merge(provided, {
        color: inputColor
      })
      return merge(baseStyles, styles?.input?.(baseStyles, state))
    },

    singleValue: (provided, state) => {
      const baseStyles = merge(
        provided,
        {
          color: valueColor
        },
        state.isDisabled && {
          color: disabledValueColor
        },
        state.selectProps.isReadOnly && {
          userSelect: 'text',
          cursor: 'text'
        }
      )
      return merge(baseStyles, styles?.singleValue?.(baseStyles, state))
    },

    option: (provided, state) => {
      const baseStyles = merge(
        provided,
        {
          ...bodyType,
          color: valueColor,
          backgroundColor: 'transparent',
          '&:active': {
            backgroundColor: toVarRef(system.color.bg.alt.strong)
          }
        },
        optionRadius && {
          // Canvas shape.round — the hover/selected highlight reads as a pill,
          // with a small vertical gap so stacked pills don't touch.
          borderRadius: optionRadius,
          marginBlock: '2px'
        },
        state.type === 'option' && state.isFocused && { backgroundColor: toVarRef(system.color.bg.alt.default) },
        state.type === 'option' &&
          state.isSelected && {
            color: 'white',
            backgroundColor: toVarRef(brand.primary.base)
          }
      )
      return merge(baseStyles, styles?.option?.(baseStyles, state))
    },

    menu: (provided, state) => {
      const baseStyles = merge(
        provided,
        {
          zIndex: zIndices.docked
        },
        menuRadius && {
          // Canvas shape.x2 — the radius Canvas assigns to popovers/cards/toasts.
          borderRadius: menuRadius
        }
      )
      return merge(baseStyles, styles?.menu?.(baseStyles, state))
    },

    menuList: (provided, state) => {
      const baseStyles = merge(
        provided,
        theme.isSanaRebrand && {
          // Inset the pill options from the rounded menu edges.
          paddingInline: '0.25rem'
        }
      )
      return merge(baseStyles, styles?.menuList?.(baseStyles, state))
    },

    menuPortal: (provided, state) => {
      const baseStyles = merge(provided, {
        zIndex: zIndices.popover
      })
      return merge(baseStyles, styles?.menuPortal?.(baseStyles, state))
    },

    indicatorSeparator: (provided, state) => {
      const baseStyles = merge(
        provided,
        {
          display: state.selectProps.isClearable && state.hasValue ? 'block' : 'none'
        },
        (state.selectProps.isReadOnly || state.isDisabled) && {
          display: 'none'
        }
      )
      return merge(baseStyles, styles?.indicatorSeparator?.(baseStyles, state))
    },

    loadingIndicator: (provided, state) => {
      const baseStyles = merge(
        provided,
        // flag-OFF keeps the legacy Chakra gray; flag-ON rides the Canvas muted fg.
        theme.isSanaRebrand ? { color: `var(${system.color.fg.muted.default})` } : { color: colors.gray[500] }
      )
      return merge(baseStyles, styles?.loadingIndicator?.(baseStyles, state))
    },

    dropdownIndicator: (provided, state) => {
      const baseStyles = merge(
        provided,
        theme.isSanaRebrand && {
          // Caret rides the value/text color (Canvas fg.default = oklch(0 0 0 / 0.77)).
          color: `var(${system.color.fg.default})`,
          '&:hover': {
            color: `var(${system.color.fg.default})`
          }
        },
        (state.selectProps.isReadOnly || state.isDisabled) && {
          display: 'none'
        }
      )
      return merge(baseStyles, styles?.dropdownIndicator?.(baseStyles, state))
    },

    clearIndicator: (provided, state) => {
      const baseStyles = merge(
        provided,
        (state.selectProps.isReadOnly || state.selectProps.isDisabled) && {
          display: 'none'
        }
      )
      return merge(baseStyles, styles?.clearIndicator?.(baseStyles, state))
    },

    multiValue: (provided, state) => {
      const baseStyles = merge(
        provided,
        {
          borderRadius: '4px'
        },
        state.isDisabled && {
          backgroundColor: 'white'
        },
        state.selectProps.isReadOnly && {
          userSelect: 'text',
          cursor: 'text'
        }
      )
      return merge(baseStyles, styles?.multiValue?.(baseStyles, state))
    },

    multiValueLabel: (provided, state) => {
      const baseStyles = merge(
        provided,
        state.isDisabled && {
          color: `var(${system.color.fg.disabled})`
        },
        (state.selectProps.isReadOnly || state.isDisabled) && {
          paddingRight: '6px'
        },
        state.selectProps.isReadOnly && {
          userSelect: 'text',
          cursor: 'text'
        }
      )
      return merge(baseStyles, styles?.multiValueLabel?.(baseStyles, state))
    },

    multiValueRemove: (provided, state) => {
      const baseStyles = merge(
        provided,
        (state.selectProps.isReadOnly || state.isDisabled) && {
          display: 'none'
        }
      )
      return merge(baseStyles, styles?.multiValueRemove?.(baseStyles, state))
    },

    valueContainer: (provided, state) => {
      const baseStyles = merge(provided, {
        minHeight: '2.375rem' // Ensures select is the same height as inputs (40px)
      })
      return merge(baseStyles, styles?.valueContainer?.(baseStyles, state))
    }
  }
}

function focusRing(color = 'transparent') {
  // If the color is transparent, we don't want to show the white ring
  const innerRing = `0px 0px 0px 2px ${color === 'transparent' ? 'transparent' : 'white'}`
  const outerRing = `0px 0px 0px 4px ${color}`
  return `${innerRing}, ${outerRing}`
}
